import React, { createContext, useState, ReactNode, useEffect } from 'react'
import { Snackbar, Alert, Slide, SlideProps } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'

type SnackbarType = 'success' | 'error'

type SnackbarContextType = {
  showMessage: (message: string, type?: SnackbarType, duration?: number) => void
}

export const SnackbarContext = createContext<SnackbarContextType>({
  showMessage: () => {
    throw new Error('showMessage method not implemented')
  }
})

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction='left' />
}

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<
    { message: string; duration: number; type: SnackbarType }[]
  >([])
  const [currentMessage, setCurrentMessage] = useState<{
    message: string
    duration: number
    type: SnackbarType
  } | null>(null)
  const [open, setOpen] = useState(false)

  const showMessage = (
    message: string,
    type: SnackbarType = 'success',
    duration = 3000
  ) => {
    setQueue(prevQueue => [...prevQueue, { message, duration, type }])
  }

  useEffect(() => {
    if (!open && queue.length > 0) {
      const nextMessage = queue[0]
      setCurrentMessage(nextMessage)
      setQueue(prevQueue => prevQueue.slice(1))
      setOpen(true)
      setTimeout(() => {
        setOpen(false)
      }, nextMessage.duration)
    }
  }, [open, queue])

  const isError = currentMessage?.type === 'error'

  return (
    <SnackbarContext.Provider value={{ showMessage }}>
      {children}
      {currentMessage && (
        <Snackbar
          open={open}
          onClose={() => setOpen(false)}
          autoHideDuration={currentMessage.duration}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            top: '90px !important',
            right: '24px !important'
          }}
        >
          <Alert
            icon={
              isError ? (
                <CancelIcon sx={{ fontSize: 22 }} />
              ) : (
                <CheckCircleIcon sx={{ fontSize: 22 }} />
              )
            }
            severity={isError ? 'error' : 'success'}
            onClose={() => setOpen(false)}
            sx={{
              width: 'fit-content',
              maxWidth: '500px',
              minWidth: '200px',
              backgroundColor: '#ffffff',
              color: '#1a1a1a',
              boxShadow:
                '0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: '10px',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              padding: '12px 16px',
              alignItems: 'center',
              fontSize: '14px',
              fontWeight: 500,
              '& .MuiAlert-icon': {
                color: isError ? '#f44336' : '#4caf50',
                marginRight: '12px',
                padding: 0
              },
              '& .MuiAlert-message': {
                padding: 0,
                fontSize: '14px',
                lineHeight: 1.5,
                whiteSpace: 'normal'
              },
              '& .MuiAlert-action': {
                padding: 0,
                marginRight: '-4px',
                marginLeft: '12px',
                '& .MuiIconButton-root': {
                  color: '#999',
                  padding: '4px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#666'
                  }
                }
              }
            }}
          >
            {currentMessage.message}
          </Alert>
        </Snackbar>
      )}
    </SnackbarContext.Provider>
  )
}
